import {
  Link,
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  sidebarConfig,
  useSidebarOpenState,
} from '@backstage/core-components';
import { devToolsAdministerPermission } from '@backstage/plugin-devtools-common';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { makeStyles } from '@material-ui/core';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import AssistantIcon from '@material-ui/icons/Assistant';
import BuildIcon from '@material-ui/icons/Build';
import CategoryIcon from '@material-ui/icons/Category';
import ComputerIcon from '@material-ui/icons/Computer';
import EqualizerIcon from '@material-ui/icons/Equalizer';
import ExtensionIcon from '@material-ui/icons/Extension';
import HomeIcon from '@material-ui/icons/Home';
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks';
import MenuIcon from '@material-ui/icons/Menu';
import PeopleIcon from '@material-ui/icons/People';
import SearchIcon from '@material-ui/icons/Search';
import TouchAppIcon from '@material-ui/icons/TouchApp';
import TrackChangesIcon from '@material-ui/icons/TrackChanges';
import WidgetsIcon from '@material-ui/icons/Widgets';
import { PropsWithChildren } from 'react';

import { DxIcon } from '@internal/backstage-plugin-dx-setup';

import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => (
  <SidebarPage>
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        <SidebarItem icon={HomeIcon} to="home" text="Home" />
        <SidebarItem icon={CategoryIcon} to="catalog" text="Catalog" />
        <MyGroupsSidebarItem
          singularTitle="My Group"
          pluralTitle="My Groups"
          icon={PeopleIcon}
        />
        <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
        <SidebarItem icon={LibraryBooksIcon} to="docs" text="Docs" />
        <SidebarItem icon={WidgetsIcon} to="self-service" text="Self Service" />
        <SidebarItem
          icon={ComputerIcon}
          to="workstation-setup"
          text="Workstation Setup"
        />
        <SidebarItem icon={DxIcon} to="dx-setup" text="DX Setup" />
        <SidebarDivider />
        <SidebarScrollWrapper>
          <SidebarItem icon={AccountTreeIcon} to="org" text="Org" />
          <SidebarItem icon={TouchAppIcon} to="prototypes" text="Prototypes" />
          <SidebarItem icon={EqualizerIcon} to="scores" text="Scores" />
          <SidebarItem
            icon={TrackChangesIcon}
            to="tech-radar"
            text="Tech Radar"
          />
          <SidebarItem
            icon={AssistantIcon}
            to="ai-assistant"
            text="AI Assistant"
          />
        </SidebarScrollWrapper>
        <SidebarDivider />
        <SidebarSpace />
        <RequirePermission
          permission={devToolsAdministerPermission}
          errorPage={<></>}
        >
          <SidebarItem icon={BuildIcon} to="devtools" text="Dev Tools" />
        </RequirePermission>
        <SidebarDivider />
        <NotificationsSidebarItem />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        <SidebarSettings />
      </SidebarGroup>
    </Sidebar>
    {children}
  </SidebarPage>
);
